/**
 * High-performance grid generator with wall patterns
 * Returns: 2D array where 0=empty, 1=wall, 2=goal
 * 
 * @param {number} gridSize - Side length of the square grid
 * @param {Array} playerPos - [x, y] coordinates of player position
 * @param {number} currentScore - Score to determine wall density
 * @returns {Array} 2D grid layout
 */
function generateGrid(gridSize, playerPos, currentScore = 0) {
    const totalCells = gridSize * gridSize;
    const flatGrid = new Uint8Array(totalCells);
    
    // Convert player [x, y] to linear index
    const playerIndex = playerPos[1] * gridSize + playerPos[0];
    
    // Place goal randomly (not on player position)
    let goalIndex;
    do {
        goalIndex = Math.floor(Math.random() * totalCells);
    } while (goalIndex === playerIndex);
    
    flatGrid[goalIndex] = 2;
    
    // Wall patterns
    const patterns = [
        [[0, 0], [1, 0], [2, 0]],
        [[0, 0], [0, 1], [0, 2]],
        [[0, 0], [1, 0], [1, 1]],
        [[0, 0], [1, 0], [1, -1]],
        [[0, 0], [1, 0], [1, 1], [0, 1]],
        [[0, 0], [1, 0], [2, 0], [3, 0]],
        [[0, 0], [0, 1], [0, 2], [0, 3]],
        [[0, 0], [1, 0], [1, 1], [1, 2]],
        [[0, 0], [1, 0], [2, 0], [2, 1]],
        [[0, 0], [0, 1], [1, 1], [1, 2]],
    ];
    const weights = [1, 1, 3, 3, 2, 1, 1, 4, 2, 2];
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    
    // Calculate wall density
    const wallFraction = 0.35 + Math.min(currentScore / 100, 0.15);
    const targetWalls = Math.floor(totalCells * wallFraction);
    
    // Build available cells
    const available = new Uint8Array(totalCells);
    for (let i = 0; i < totalCells; i++) {
        if (i !== playerIndex && i !== goalIndex) {
            available[i] = 1;
        }
    }
    
    let wallCount = 0;
    let attempts = 500;
    
    while (wallCount < targetWalls && attempts > 0) {
        attempts--;
        
        // Pick random available cell
        let originIndex = -1;
        let tries = 0;
        while (tries < 50 && originIndex === -1) {
            const candidate = Math.floor(Math.random() * totalCells);
            if (available[candidate]) {
                originIndex = candidate;
            }
            tries++;
        }
        
        if (originIndex === -1) break;
        
        const originRow = Math.floor(originIndex / gridSize);
        const originCol = originIndex % gridSize;
        
        // Weighted random pattern selection
        const r = Math.random() * totalWeight;
        let acc = 0;
        let pattern = patterns[0];
        for (let i = 0; i < patterns.length; i++) {
            acc += weights[i];
            if (r < acc) {
                pattern = patterns[i];
                break;
            }
        }
        
        // Validate pattern placement
        const tiles = [];
        let valid = true;
        
        for (let i = 0; i < pattern.length; i++) {
            const [rowOff, colOff] = pattern[i];
            const newRow = originRow + rowOff;
            const newCol = originCol + colOff;
            
            if (newRow < 0 || newRow >= gridSize || 
                newCol < 0 || newCol >= gridSize) {
                valid = false;
                break;
            }
            
            const idx = newRow * gridSize + newCol;
            if (flatGrid[idx] !== 0 || idx === playerIndex || idx === goalIndex) {
                valid = false;
                break;
            }
            
            tiles.push(idx);
        }
        
        if (!valid) continue;
        
        // Check path validity
        if (isPathValidFast(playerIndex, goalIndex, flatGrid, tiles, gridSize)) {
            // Place walls
            for (let i = 0; i < tiles.length; i++) {
                flatGrid[tiles[i]] = 1;
                wallCount++;
                
                // Remove from available (including neighbors)
                const tileRow = Math.floor(tiles[i] / gridSize);
                const tileCol = tiles[i] % gridSize;
                
                for (let dy = -1; dy <= 1; dy++) {
                    for (let dx = -1; dx <= 1; dx++) {
                        const nRow = tileRow + dy;
                        const nCol = tileCol + dx;
                        if (nRow >= 0 && nRow < gridSize && 
                            nCol >= 0 && nCol < gridSize) {
                            available[nRow * gridSize + nCol] = 0;
                        }
                    }
                }
            }
        }
    }
    
    // Convert flat grid to 2D array
    const grid2D = [];
    for (let row = 0; row < gridSize; row++) {
        const rowArray = [];
        for (let col = 0; col < gridSize; col++) {
            rowArray.push(flatGrid[row * gridSize + col]);
        }
        grid2D.push(rowArray);
    }
    
    return grid2D;
}

function isPathValidFast(start, end, grid, tempWalls, gridSize) {
    const totalCells = gridSize * gridSize;
    const queue = new Uint32Array(totalCells);
    const visited = new Uint8Array(totalCells);
    
    let qHead = 0;
    let qTail = 0;
    
    queue[qTail++] = start;
    visited[start] = 1;
    
    for (let i = 0; i < tempWalls.length; i++) {
        visited[tempWalls[i]] = 1;
    }
    
    while (qHead < qTail) {
        const curr = queue[qHead++];
        
        if (curr === end) return true;
        
        const row = Math.floor(curr / gridSize);
        const col = curr % gridSize;
        
        if (row > 0) {
            const neighbor = curr - gridSize;
            if (!visited[neighbor] && grid[neighbor] !== 1) {
                visited[neighbor] = 1;
                queue[qTail++] = neighbor;
            }
        }
        if (row < gridSize - 1) {
            const neighbor = curr + gridSize;
            if (!visited[neighbor] && grid[neighbor] !== 1) {
                visited[neighbor] = 1;
                queue[qTail++] = neighbor;
            }
        }
        if (col > 0) {
            const neighbor = curr - 1;
            if (!visited[neighbor] && grid[neighbor] !== 1) {
                visited[neighbor] = 1;
                queue[qTail++] = neighbor;
            }
        }
        if (col < gridSize - 1) {
            const neighbor = curr + 1;
            if (!visited[neighbor] && grid[neighbor] !== 1) {
                visited[neighbor] = 1;
                queue[qTail++] = neighbor;
            }
        }
    }
    
    return false;
}

export default generateGrid;